/// <reference lib="dom"/>
import * as rxjs from "rxjs";
import { WbaxRoot } from "@chi/wbax";
import {
  type ActionHandlers,
  Dispatcher,
  type DispatcherOptions,
  type Message,
} from "@utils/dispatch";
import type { WbaCtx } from "@/context/wbactx.tsx";
import { FxLoader } from "@/synth/fxChain.tsx";
import { type SampleGen, SampleGenTree } from "@/synth/sampleGen.tsx";

//#region dispatch
export const SampleGenPreviewStatuses = ["stopped", "playing"] as const;
export type SampleGenPreviewStatus = (typeof SampleGenPreviewStatuses)[number];

export interface SampleGenPreviewState {
  status: SampleGenPreviewStatus;
  loop: boolean;
  startedAt: number | null;
  error: string | null;
}

export const SampleGenPreviewDefaultState: SampleGenPreviewState = {
  status: "stopped",
  loop: true,
  startedAt: null,
  error: null,
};

export interface SampleGenPreviewActions {
  play: undefined;
  stop: undefined;
  restart: undefined;
  loop: { loop: boolean };
  ended: undefined;
  error: { message: string };
}

export type SampleGenPreviewMessage = Message<SampleGenPreviewActions>;
type Handlers = ActionHandlers<SampleGenPreviewActions, SampleGenPreviewState>;
//#endregion

const LookAhead = 0.2;
const Tail = 2;

function ErrorMessage(e: unknown) {
  return e instanceof Error ? e.message : String(e);
}

export interface SampleGenPreviewProps {
  wbactx: WbaCtx;
  gen: SampleGen;
  base?: string;
  initial?: Partial<SampleGenPreviewState>;
  dispatcher?: DispatcherOptions;
}

interface PreviewPass {
  root: WbaxRoot;
  start: number;
}

export class SampleGenPreview
  extends Dispatcher<SampleGenPreviewActions, SampleGenPreviewState, SampleGenPreviewProps> {
  protected readonly handlers: Handlers = {
    play: ({ state }) => {
      if (state.status === "playing") return state;
      return { ...state, status: "playing", startedAt: this.#start(), error: null };
    },
    stop: ({ state }) => {
      this.#stop();
      return { ...state, status: "stopped", startedAt: null };
    },
    restart: ({ state }) => {
      this.#stop();
      return { ...state, status: "playing", startedAt: this.#start(), error: null };
    },
    loop: ({ state, payload }) => ({ ...state, loop: payload.loop }),
    ended: ({ state }) => {
      this.#stop();
      return { ...state, status: "stopped", startedAt: null };
    },
    error: ({ state, payload }) => ({ ...state, error: payload.message }),
  };

  #subs = new rxjs.Subscription();
  #loader: FxLoader;
  #passes: PreviewPass[] = [];
  #timer: ReturnType<typeof setTimeout> | null = null;
  #output: GainNode | null = null;

  constructor(props: SampleGenPreviewProps) {
    super(props, { ...SampleGenPreviewDefaultState, ...props.initial }, props.dispatcher);
    this.#loader = new FxLoader({ wbactx: props.wbactx, base: props.base });
    this.#subs.add(
      this.#loader.error$.subscribe((message) =>
        this.dispatch({ type: "error", payload: { message } })
      ),
    );
    this.#subs.add(
      rxjs.combineLatest([props.gen.state$, this.#loader.changed$]).subscribe(() =>
        this.#rerender()
      ),
    );
  }

  #tree() {
    return SampleGenTree(this.props.gen.state, {
      assets: this.#loader.assets(),
      ready: (slot) => this.#loader.ready(slot),
    });
  }

  #rerender() {
    for (const pass of this.#passes) {
      try {
        pass.root.render(this.#tree());
      } catch (e) {
        this.dispatch({ type: "error", payload: { message: ErrorMessage(e) } });
      }
    }
  }

  #start(): number {
    const { wbactx } = this.props;
    if (wbactx.state["ctx/status"] !== "running") wbactx.dispatch({ type: "ctx/resume" });
    if (!this.#output) {
      this.#output = wbactx.node((c) => c.createGain(), {
        id: "/sampleGen/preview",
        name: "sample preview",
      });
      this.#output.connect(wbactx.output);
    }
    const start = wbactx.ctx.currentTime + 0.05;
    this.#pass(start);
    return start;
  }

  #pass(start: number) {
    const root = new WbaxRoot({
      ctx: this.props.wbactx.ctx as unknown as BaseAudioContext,
      destination: this.#output!,
      time: start,
    });
    this.#passes.push({ root, start });
    try {
      root.render(this.#tree());
    } catch (e) {
      this.dispatch({ type: "error", payload: { message: ErrorMessage(e) } });
    }

    const end = start + this.props.gen.state.length;
    const delay = (end - LookAhead - this.props.wbactx.ctx.currentTime) * 1000;
    this.#timer = setTimeout(() => this.#next(end), Math.max(0, delay));
  }

  #next(end: number) {
    this.#timer = null;
    const now = this.props.wbactx.ctx.currentTime;
    const keep: PreviewPass[] = [];
    for (const pass of this.#passes) {
      if (pass.start + this.props.gen.state.length + Tail < now) pass.root.dispose();
      else keep.push(pass);
    }
    this.#passes = keep;

    if (this.state.loop) this.#pass(end);
    else this.#timer = setTimeout(() => this.dispatch({ type: "ended" }), LookAhead * 1000);
  }

  #stop() {
    if (this.#timer) clearTimeout(this.#timer);
    this.#timer = null;
    for (const pass of this.#passes) pass.root.dispose();
    this.#passes = [];
  }

  override dispose() {
    if (this.disposed) return;
    this.#stop();
    this.#subs.unsubscribe();
    this.#loader.dispose();
    if (this.#output) this.props.wbactx.release(this.#output);
    this.#output = null;
    super.dispose();
  }
}
